import { useEffect, useState, useCallback } from "react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/Header";
import { StatusBadge } from "@/components/StatusBadge";
import client from "@/api";
import { toast } from "sonner";
import { GraduationCap, Lock, AlertTriangle, CheckCircle2, XCircle, ArrowLeft, ArrowRight, RefreshCw, Download } from "lucide-react";

async function downloadResultPdf(t) {
  try {
    const res = await client.get("/result/pdf", { responseType: "blob" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(res.data);
    link.download = "Bartender_Master_Level.pdf";
    document.body.appendChild(link); link.click(); link.remove();
    URL.revokeObjectURL(link.href);
  } catch (e) {
    toast.error(e.response?.data?.detail || t("no_result"));
  }
}

export default function FinalExam() {
  const { t, lang } = useApp();
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("status"); // status | exam | result
  const [exam, setExam] = useState(null);
  const [answers, setAnswers] = useState({});
  const [idx, setIdx] = useState(0);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = useCallback(async () => {
    const { data } = await client.get("/final/status");
    setStatus(data);
    if (data.has_open_attempt) { setMode("status"); }
  }, []);

  useEffect(() => { loadStatus().catch(() => toast.error(t("error"))); }, [loadStatus]);

  const openAttempt = async (attemptId) => {
    const { data } = await client.get(`/final/attempt/${attemptId}?lang=${lang}`);
    setExam(data); setAnswers(data.answers || {}); setIdx(0); setMode("exam");
  };

  const start = async () => {
    if (!window.confirm(t("final_start_confirm"))) return;
    setBusy(true);
    try {
      const { data } = await client.post(`/final/start?lang=${lang}`);
      await openAttempt(data.attempt_id);
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); await loadStatus(); }
    finally { setBusy(false); }
  };

  const saveAnswer = async (qid, oid) => {
    setAnswers((a) => ({ ...a, [qid]: oid }));
    try { await client.post(`/final/attempt/${exam.attempt_id}/answer`, { question_id: qid, option_id: oid }); } catch (_) {}
  };

  const submit = async () => {
    const unanswered = exam.questions.filter((q) => !answers[q.question_id]).length;
    if (unanswered > 0 && !window.confirm(`${unanswered} ${t("unanswered_count")}. ${t("unanswered_warn")}`)) return;
    setBusy(true);
    try {
      const { data } = await client.post(`/final/attempt/${exam.attempt_id}/submit`, { answers });
      setResult(data); setMode("result"); window.scrollTo(0, 0);
      await loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || t("error")); }
    finally { setBusy(false); }
  };

  const requestReattempt = async () => {
    try { await client.post("/final/request-reattempt", {}); toast.success(t("reattempt_requested")); await loadStatus(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  if (!status) return <Shell><div className="animate-pulse h-64 bg-[#1a1a1a] rounded-2xl" /></Shell>;

  if (mode === "result" && result) return <Shell><FinalResult result={result} /></Shell>;

  if (mode === "exam" && exam) {
    const q = exam.questions[idx];
    const answeredCount = Object.values(answers).filter(Boolean).length;
    return (
      <Shell>
        <div className="flex items-center justify-between mb-4">
          <h1 className="font-serif text-2xl text-[#FFFFFF]">{t("final_title")}</h1>
          <span className="text-sm text-[#C9CDD3]">{answeredCount}/{exam.total}</span>
        </div>
        <div className="w-full h-1.5 bg-[#1a1a1a] rounded-full mb-8 overflow-hidden">
          <div className="h-full bg-[#D21F2A] transition-all" style={{ width: `${((idx + 1) / exam.total) * 100}%` }} />
        </div>
        <div className="card-dark rounded-2xl p-6 sm:p-8 fade-up" data-testid="final-question">
          <div className="text-xs uppercase tracking-wider text-[#D21F2A] mb-3">{t("question")} {idx + 1} {t("of")} {exam.total}</div>
          <h2 className="font-serif text-xl sm:text-2xl text-[#FFFFFF] mb-6">{q.prompt}</h2>
          <div className="space-y-3" role="radiogroup">
            {q.options.map((o) => {
              const sel = answers[q.question_id] === o.option_id;
              return (
                <button key={o.option_id} role="radio" aria-checked={sel} data-testid={`final-option-${o.option_id}`}
                  onClick={() => saveAnswer(q.question_id, o.option_id)}
                  className={`focusable w-full text-left px-5 py-4 rounded-xl border transition-colors flex items-center gap-3 ${sel ? "border-[#D21F2A] bg-[#D21F2A]/10" : "border-[#D21F2A]/15 bg-[#141414] hover:border-[#D21F2A]/40"}`}>
                  <span className={`w-5 h-5 rounded-full border-2 shrink-0 flex items-center justify-center ${sel ? "border-[#D21F2A]" : "border-[#9AA0A6]"}`}>
                    {sel && <span className="w-2.5 h-2.5 rounded-full bg-[#D21F2A]" />}
                  </span>
                  <span className="text-[#FFFFFF]">{o.text}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="flex items-center justify-between mt-6">
          <button data-testid="final-prev-btn" disabled={idx === 0} onClick={() => setIdx((i) => i - 1)}
            className="focusable px-4 py-2.5 rounded-lg border border-[#D21F2A]/20 text-[#FFFFFF] disabled:opacity-40 flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> {t("prev")}</button>
          {idx < exam.total - 1 ? (
            <button data-testid="final-next-btn" onClick={() => setIdx((i) => i + 1)}
              className="btn-gold focusable px-5 py-2.5 rounded-lg flex items-center gap-1">{t("next")} <ArrowRight className="w-4 h-4" /></button>
          ) : (
            <button data-testid="final-submit-btn" disabled={busy} onClick={submit}
              className="btn-gold focusable px-6 py-2.5 rounded-lg">{busy ? t("loading") : t("final_submit")}</button>
          )}
        </div>
      </Shell>
    );
  }

  // Status screen
  const { rules } = status;
  return (
    <Shell>
      <div className="text-center mb-8">
        <GraduationCap className="w-14 h-14 text-[#D21F2A] mx-auto mb-4" />
        <h1 className="font-serif text-3xl sm:text-4xl text-[#FFFFFF]">{t("final_title")}</h1>
      </div>

      {status.has_open_attempt ? (
        <div className="card-dark rounded-2xl p-8 text-center" data-testid="final-resume-card">
          <StatusBadge status="in_progress" label={t("status_in_progress")} />
          <p className="text-[#C9CDD3] my-5">{t("final_one_attempt")}</p>
          <button data-testid="final-resume-btn" onClick={() => openAttempt(status.open_attempt_id)}
            className="btn-gold focusable px-6 py-3 rounded-lg">{t("final_resume")}</button>
        </div>
      ) : !status.eligible ? (
        <div className="card-dark rounded-2xl p-8 text-center" data-testid="final-not-eligible">
          <Lock className="w-10 h-10 text-[#9AA0A6] mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-[#FFFFFF] mb-2">{t("final_not_eligible")}</h2>
          <p className="text-[#C9CDD3]">{t("final_not_eligible_desc")}</p>
          <p className="text-sm text-[#FDE047] mt-3">{t("final_missing", { n: status.missing_chapters.length })}</p>
        </div>
      ) : status.can_start ? (
        <div className="card-dark rounded-2xl p-8" data-testid="final-rules-card">
          <h2 className="font-serif text-2xl text-[#FFFFFF] mb-4">{t("final_rules")}</h2>
          <div className="flex items-start gap-2 rounded-xl border border-[#E0A106] bg-[#332208] text-[#FDE047] px-4 py-3 mb-5 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> <span>{t("final_one_attempt")}</span>
          </div>
          <ul className="space-y-2 text-[#C9CDD3] mb-6">
            <li>• {t("final_rule_1", { n: rules.question_count })}</li>
            <li>• {t("final_rule_2", { n: rules.pass_mark })}</li>
            <li>• {t("final_rule_3")}</li>
            <li>• {t("final_rule_4")}</li>
          </ul>
          <button data-testid="final-start-btn" disabled={busy} onClick={start}
            className="btn-gold focusable w-full py-4 rounded-xl">{busy ? t("loading") : t("final_start")}</button>
        </div>
      ) : (
        <div className="card-dark rounded-2xl p-8 text-center" data-testid="final-locked">
          <Lock className="w-10 h-10 text-[#D8B4FE] mx-auto mb-4" />
          <h2 className="font-serif text-2xl text-[#FFFFFF] mb-2">{t("final_locked")}</h2>
          <p className="text-[#C9CDD3] mb-5">{t("final_locked_desc")}</p>
          {status.pending_reattempt_request ? (
            <StatusBadge status="reattempt" label={t("reattempt_requested")} testid="reattempt-pending-badge" />
          ) : (
            <button data-testid="request-reattempt-btn" onClick={requestReattempt}
              className="focusable px-6 py-3 rounded-lg border border-[#D21F2A]/30 text-[#FFFFFF] inline-flex items-center gap-2"><RefreshCw className="w-4 h-4" /> {t("request_reattempt")}</button>
          )}
        </div>
      )}

      {/* History */}
      {status.history?.filter((h) => h.status === "submitted").length > 0 && (
        <div className="mt-8" data-testid="final-history">
          <h3 className="font-serif text-xl text-[#FFFFFF] mb-3">{t("dash_recent")}</h3>
          {status.history.filter((h) => h.status === "submitted").map((h, i) => (
            <div key={i} className="flex items-center justify-between px-4 py-3 rounded-xl bg-[#141414] border border-[#D21F2A]/10 mb-2">
              <StatusBadge status={h.passed ? "passed" : "failed"} label={h.passed ? t("passed") : t("failed")} />
              <span className="font-serif text-lg text-[#D21F2A]">{h.percent}%</span>
            </div>
          ))}
          <button data-testid="download-result-pdf-history" onClick={() => downloadResultPdf(t)}
            className="btn-gold focusable mt-3 px-5 py-2.5 rounded-lg inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> {t("download_my_result")}
          </button>
          <p className="text-xs text-[#9AA0A6] mt-3">{t("no_solution_key")}</p>
        </div>
      )}
    </Shell>
  );
}

function FinalResult({ result }) {
  const { t } = useApp();
  return (
    <div className="fade-up">
      <div className="card-dark rounded-2xl p-8 text-center" data-testid="final-result">
        <StatusBadge status={result.passed ? "passed" : "failed"} label={result.passed ? t("passed") : t("failed")} testid="final-pass-badge" />
        <div className="font-serif text-6xl text-[#FFFFFF] my-4">{result.percent}%</div>
        <p className="text-[#C9CDD3]">{result.correct_count} / {result.total} {t("correct_answers")} · {t("final_pass_mark")}: {result.pass_mark}%</p>
        <button data-testid="download-result-pdf" onClick={() => downloadResultPdf(t)}
          className="btn-gold focusable mt-5 px-6 py-3 rounded-lg inline-flex items-center gap-2">
          <Download className="w-5 h-5" /> {t("download_my_result")}
        </button>
      </div>
      <div className="mt-6 card-dark rounded-2xl p-6" data-testid="final-per-chapter">
        <h3 className="font-serif text-xl text-[#FFFFFF] mb-4">{t("per_chapter")}</h3>
        <div className="space-y-2">
          {result.per_chapter.map((c, i) => (
            <div key={i} className="flex items-center gap-3">
              <span className="text-sm text-[#C9CDD3] flex-1 truncate">{c.chapter_title}</span>
              <span className="text-xs text-[#9AA0A6]">{c.correct}/{c.total}</span>
              <div className="w-24 h-1.5 bg-[#1a1a1a] rounded-full overflow-hidden">
                <div className="h-full bg-[#D21F2A]" style={{ width: `${c.percent}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      <p className="text-xs text-[#9AA0A6] mt-4">{t("no_solution_key")}</p>
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-[#0d0d0d] bg-grain">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-10">{children}</main>
    </div>
  );
}
